import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MailIcon } from 'lucide-react';
import { useAuth } from '../hooks';
import { Alert, MockdLogo } from '../components/common';

export function ForgotPassword() {
  const { requestPasswordReset, loading, error, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await requestPasswordReset(email);
      setSentTo(email);
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleResend = async () => {
    if (!sentTo) return;
    try {
      await requestPasswordReset(sentTo);
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleUseDifferentEmail = () => {
    clearError();
    setSentTo(null);
  };

  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <MockdLogo size="md" className="mx-auto" />
          <h1 className="mt-4 text-base-content">Reset your password</h1>
        </div>

        <div className="p-6">
          {sentTo ? (
            <div className="text-center">
              {error && (
                <Alert variant="error" className="mb-4">{error}</Alert>
              )}

              <div className="w-12 h-12 mx-auto mb-4 bg-success/20 rounded-full flex items-center justify-center">
                <MailIcon className="size-6 text-success" />
              </div>
              <h2 className="text-base-content mb-2">Check your email</h2>
              <p className="text-base-content/80 mb-6">
                If an account exists for <span className="font-medium text-base-content">{sentTo}</span>,
                we sent a password reset link. The link expires in 1 hour.
              </p>

              <button
                onClick={handleResend}
                disabled={loading}
                className="btn btn-outline w-full"
              >
                {loading ? 'Sending...' : 'Resend link'}
              </button>

              <div className="mt-4">
                <button
                  onClick={handleUseDifferentEmail}
                  className="link link-primary text-sm"
                >
                  Use a different email
                </button>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <Alert variant="error" className="mb-4">{error}</Alert>
              )}

              <p className="text-base-content/80 mb-4 text-sm">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="fieldset">
                  <label htmlFor="email" className="fieldset-legend">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="input w-full"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary w-full"
                  >
                    {loading ? 'Sending...' : 'Send reset link'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        <div className="mt-4 text-center">
          <Link to="/login" className="link link-hover text-sm text-base-content/80">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
